import PublicLayout from '../components/PublicLayout';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

export default function TermsOfServicePage() {
  useDocumentMeta(
    'Terms of Service',
    'Terms of Service for Takedown League, the free fantasy wrestling platform. Read our terms covering account usage, conduct, intellectual property, and more.'
  );

  return (
    <PublicLayout>
      <div className="bg-gray-50 py-12 px-4">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: February 10, 2026</p>

          <div className="space-y-8 text-gray-700 text-sm leading-relaxed">
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">1. Acceptance of Terms</h2>
              <p>
                By accessing or using Takedown League ("the Service"), available at
                takedownleague.com, you agree to be bound by these Terms of Service ("Terms"). If
                you do not agree to these Terms, you may not access or use the Service. We reserve
                the right to update these Terms at any time. Continued use of the Service after
                changes are posted constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                2. Description of Service
              </h2>
              <p className="mb-3">
                Takedown League is a free fantasy sports platform focused on wrestling. Users can
                create and join leagues, draft real wrestlers to a roster, and earn points based on
                those wrestlers' performances in actual tournaments. The Service is provided for
                entertainment and community engagement purposes.
              </p>
              <p>
                Takedown League is not a gambling service. There are no entry fees, no monetary
                wagers, and no cash prizes associated with league participation. All competition on
                the platform is for entertainment and bragging rights only.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">3. Account Registration</h2>
              <p className="mb-3">
                To use certain features of the Service, you must create an account. You agree to
                provide accurate and complete information during registration and to keep your
                account information up to date. You are responsible for maintaining the
                confidentiality of your account credentials and for all activity that occurs under
                your account.
              </p>
              <p>
                You must be at least 13 years of age to create an account. If you are under 18, you
                represent that you have parental or guardian consent to use the Service. We reserve
                the right to terminate accounts that we reasonably believe are operated by users
                under the minimum age requirement.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">4. User Conduct</h2>
              <p className="mb-3">You agree not to use the Service to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Violate any applicable local, state, national, or international law.</li>
                <li>
                  Harass, abuse, or threaten other users or engage in any conduct that is harmful or
                  objectionable.
                </li>
                <li>
                  Impersonate another person or entity, or falsely represent your affiliation with a
                  person or entity.
                </li>
                <li>
                  Attempt to gain unauthorized access to other users' accounts or to any systems or
                  networks connected to the Service.
                </li>
                <li>
                  Use automated scripts, bots, or other tools to access the Service or collect data
                  from it without our express written permission.
                </li>
                <li>
                  Interfere with or disrupt the Service or servers or networks connected to the
                  Service.
                </li>
                <li>
                  Use the Service for any form of gambling, wagering, or staking real money on
                  league outcomes.
                </li>
              </ul>
            </section>

            {/* Ad Banner - Mid-terms placement */}
            <div className="my-8">
              <AdBanner slot={AD_SLOTS.termsOfService} format="horizontal" />
            </div>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">5. Intellectual Property</h2>
              <p className="mb-3">
                All content, features, and functionality of the Service, including but not limited
                to text, graphics, logos, icons, images, and software, are the property of Takedown
                League or its licensors and are protected by United States and international
                copyright, trademark, and other intellectual property laws.
              </p>
              <p>
                You may not reproduce, distribute, modify, create derivative works of, publicly
                display, or otherwise exploit any content from the Service without our prior written
                consent. Your use of the Service does not grant you ownership of any intellectual
                property rights in the Service or its content.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">6. User Content</h2>
              <p>
                Any content you submit to the Service, including display names and league names,
                remains your property. However, by submitting content to the Service, you grant
                Takedown League a non-exclusive, worldwide, royalty-free license to use, display,
                and distribute that content in connection with operating and providing the Service.
                You represent that you have the right to submit any content you provide and that
                such content does not violate the rights of any third party.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                7. No Gambling Disclaimer
              </h2>
              <p>
                Takedown League is strictly a fantasy sports entertainment platform. No real money,
                prizes, or items of monetary value are awarded through league competition on our
                platform. Users are prohibited from using the Service as a platform for any form of
                gambling or wagering. Any external arrangements made between users regarding league
                outcomes are not endorsed, facilitated, or supported by Takedown League, and we bear
                no responsibility for such arrangements.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                8. Disclaimers and Limitation of Liability
              </h2>
              <p className="mb-3">
                The Service is provided on an "as is" and "as available" basis without warranties of
                any kind, either express or implied. We do not warrant that the Service will be
                uninterrupted, error-free, or free of harmful components.
              </p>
              <p className="mb-3">
                Tournament results, scores, and standings are provided based on data available to us
                and may contain inaccuracies or delays. Takedown League is not responsible for
                errors in scoring or standings caused by incorrect or delayed tournament data.
              </p>
              <p>
                To the fullest extent permitted by law, Takedown League and its operators shall not
                be liable for any indirect, incidental, special, consequential, or punitive damages,
                or any loss of profits or revenues, whether incurred directly or indirectly, arising
                from your use of or inability to use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">9. Termination</h2>
              <p className="mb-3">
                We reserve the right to suspend or terminate your account and access to the Service
                at our sole discretion, without notice, for conduct that we determine violates these
                Terms or is harmful to other users, us, or third parties, or for any other reason.
              </p>
              <p>
                You may delete your account at any time by contacting us. Upon termination, your
                right to use the Service will immediately cease. Provisions of these Terms that by
                their nature should survive termination will remain in effect.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">10. Governing Law</h2>
              <p>
                These Terms are governed by and construed in accordance with the laws of the United
                States. Any disputes arising from these Terms or your use of the Service shall be
                resolved in the courts of the applicable jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">11. Contact</h2>
              <p>
                If you have questions about these Terms of Service, please contact us through our{' '}
                <a
                  href="https://takedownleague.tawk.help/category/getting-started"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 underline"
                >
                  Help Center
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
